// Contacts & record — the client's editable record fields (structured address etc.),
// the contact table with its editor modal, and the archive/delete disposal row.
// Extracted from ClientsPage's slide-in inspector when the client became a page; the
// logic — including the contact save guard on first/last name (the derived `name` field
// refused every new contact after the name split) — is unchanged.
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Archive, Trash2 } from 'lucide-react';
import { useToast } from './Toast.js';
import ConfirmModal from './ConfirmModal.js';
import { COUNTRIES, normalisePhone } from '../../shared/contact.js';
import { LANGUAGES, LANGUAGE_LABEL, type Language } from '../../shared/language.js';
import * as crm from '../api/crm.js';
import type { Contact } from '../api/crm.js';
import { useT } from '../i18n/index.js';
import './ClientContactsRecord.de.js';
import './ClientContactsRecord.fr.js';

/** `t()` for a sentence with markup inside: each `{name}` placeholder is filled with a React node. */
function tx(t: (key: string) => string, key: string, nodes: Record<string, ReactNode>): ReactNode {
  return t(key).split(/(\{\w+\})/g).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return m ? <Fragment key={i}>{nodes[m[1]]}</Fragment> : part;
  });
}

const CLIENT_STATUSES: Array<crm.Client['status']> = ['active', 'archived'];

type ContactDraft = Partial<Contact> & { name?: string };

interface Props {
  detail: crm.ClientDetail;
  /** Reload the detail after a mutation — the page owns the fetch. */
  onChanged: () => Promise<void> | void;
  /** The client was hard-deleted — there is nothing left to stand on; go back to the list. */
  onDeleted: () => void;
}

export default function ClientContactsRecord({ detail, onChanged, onDeleted }: Props) {
  const toast = useToast();
  const t = useT();
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);

  /** PNG/JPEG only — the PPTX embeds the file as-is, and a light mark reads best on the dark cover. */
  async function pickLogo(file: File) {
    setLogoBusy(true);
    try {
      await crm.uploadClientLogo(detail.id, file);
      setLogoVersion((n) => n + 1);
      await onChanged();
      toast('ok', t('Logo saved — the deck cover uses it.'));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setLogoBusy(false);
    }
  }

  async function removeLogo() {
    setLogoBusy(true);
    try {
      await crm.removeClientLogo(detail.id);
      await onChanged();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setLogoBusy(false);
    }
  }

  const [edit, setEdit] = useState<Partial<crm.Client>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [contactDraft, setContactDraft] = useState<ContactDraft | null>(null);
  const [phoneNote, setPhoneNote] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<
    | { kind: 'archive-client'; id: number; label: string }
    | { kind: 'delete-client'; id: number; label: string; plan: crm.ClientDeletionPlan }
    | { kind: 'delete-contact'; id: number; label: string; plan: crm.DeletionPlan }
    | null
  >(null);
  const [busy, setBusy] = useState(false);

  // The form mirrors the record; a reload after any mutation re-seeds it (matching the
  // inspector's behaviour, where loadDetail reset the draft).
  useEffect(() => {
    setEdit({
      name: detail.name, legal_name: detail.legal_name, short_name: detail.short_name, sector: detail.sector,
      address_line1: detail.address_line1, address_line2: detail.address_line2,
      city: detail.city, postcode: detail.postcode, country: detail.country,
      notes: detail.notes, status: detail.status, language: detail.language,
    });
    setDirty(false);
  }, [detail]);

  function field<K extends keyof crm.Client>(k: K, v: crm.Client[K]) {
    setEdit((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
  }

  async function saveClient() {
    if (!dirty) return;
    setSaving(true);
    try {
      await crm.updateClient(detail.id, edit);
      toast('ok', t('Client saved'));
      setDirty(false);
      await onChanged();
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveContact() {
    // The form writes first_name/last_name; `name` is DERIVED server-side. Guarding on the
    // derived field refused every new contact after the name split.
    const hasName = !!(contactDraft?.first_name?.trim() || contactDraft?.last_name?.trim() || contactDraft?.name?.trim());
    if (!contactDraft || !hasName) { toast('warn', t('A first or family name is required')); return; }
    try {
      if (contactDraft.id) await crm.updateContact(detail.id, contactDraft.id, contactDraft);
      else await crm.createContact(detail.id, contactDraft);
      toast('ok', t('Contact saved'));
      setContactDraft(null);
      await onChanged();
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  /** Fetch the plan, then open the dialog — never open a dialog that cannot answer "why not". */
  async function askDeleteClient() {
    try {
      setConfirm({ kind: 'delete-client', id: detail.id, label: detail.name, plan: await crm.clientDeletionPlan(detail.id) });
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  async function askDeleteContact(id: number, label: string) {
    try {
      setConfirm({ kind: 'delete-contact', id, label, plan: await crm.contactDeletionPlan(detail.id, id) });
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  async function runConfirm() {
    if (!confirm) return;
    // A blocked plan's confirm button performs the ALTERNATIVE the blocker names, so the
    // operator is never left staring at a dialog that only knows how to say no.
    const blocked = confirm.kind !== 'archive-client' && !confirm.plan.allowed;
    if (blocked && confirm.kind === 'delete-contact') { setConfirm(null); return; }
    const removesClient = confirm.kind === 'delete-client' && !blocked;
    setBusy(true);
    try {
      if (confirm.kind === 'archive-client' || (blocked && confirm.kind === 'delete-client')) {
        await crm.archiveClient(confirm.id);
        toast('ok', t('Client archived — it keeps its history and leaves the active list'));
      } else if (confirm.kind === 'delete-client') {
        const out = await crm.deleteClient(confirm.id);
        toast('ok', t('{label} deleted — {cascade} went with it', {
          label: confirm.label,
          cascade: out.cascade.map((c) => `${c.count} ${c.what}`).join(', ') || t('nothing else'),
        }));
      } else {
        await crm.deleteContact(detail.id, confirm.id);
        toast('ok', t('{label} deleted', { label: confirm.label }));
      }
      setConfirm(null);
      if (removesClient) onDeleted();
      else await onChanged();
    } catch (err) {
      toast('err', (err as Error).message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* ---- Contacts — first: the people are what the operator comes here for ---- */}
      <div className="flex justify-between items-center mb-8">
        <h4 className="section-label">{t('Contacts')}</h4>
        <button
          className="btn btn-sm"
          onClick={() => setContactDraft({ name: '', is_sponsor: false })}
        >
          {t('Add contact')}
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-compact mb-24">
          <thead>
            <tr>
              <th>{t('Name')}</th><th>{t('Role')}</th><th>{t('Email')}</th><th style={{ width: 120 }}>{t('Phone')}</th>
              <th style={{ width: 70 }}>{t('Sponsor')}</th><th style={{ width: 104 }} />
            </tr>
          </thead>
          <tbody>
            {detail.contacts.map((c) => (
              <tr key={c.id}>
                <td className="font-medium text-ink-1">{c.name}</td>
                <td className="text-sm text-ink-2">{c.role_title ?? '—'}</td>
                <td className="text-xs text-ink-2 truncate">{c.email ?? '—'}</td>
                <td className="mono text-xs text-ink-3">{c.phone ?? '—'}</td>
                <td>{c.is_sponsor ? <span className="badge badge-accent">{t('sponsor')}</span> : null}</td>
                <td className="text-right">
                  <button className="btn btn-sm btn-ghost" onClick={() => setContactDraft({ ...c })}>{t('Edit')}</button>
                  <button
                    className="btn btn-sm btn-ghost"
                    title={t('Delete {name}', { name: c.name })}
                    onClick={() => void askDeleteContact(c.id, c.name)}
                  >
                    <Trash2 className="icon-sm" />
                  </button>
                </td>
              </tr>
            ))}
            {!detail.contacts.length && (
              <tr><td colSpan={6} className="text-sm text-ink-3">{t('No contacts — gate G0 needs at least one.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---- Editable record fields ---- */}
      <h4 className="section-label mb-8">{t('Record')}</h4>
      <div className="flex gap-12">
        <div className="form-group flex-1">
          <label className="form-label">{t('Name')}</label>
          <input className="form-input" value={edit.name ?? ''} onChange={(e) => field('name', e.target.value)} />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label className="form-label">{t('Short name')}</label>
          <input
            className="form-input mono"
            value={edit.short_name ?? ''}
            onChange={(e) => field('short_name', e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-12">
        <div className="form-group flex-1">
          <label className="form-label">{t('Sector')}</label>
          <input className="form-input" value={edit.sector ?? ''} onChange={(e) => field('sector', e.target.value)} />
        </div>
        <div className="form-group" style={{ width: 150 }}>
          <label className="form-label" title={t('Every engagement of this client inherits it; an engagement may change its own.')}>{t('Correspondence language')}</label>
          <select
            className="form-select"
            value={edit.language ?? 'de'}
            onChange={(e) => field('language', e.target.value as Language)}
          >
            {LANGUAGES.map((l) => <option key={l} value={l}>{LANGUAGE_LABEL[l]}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label className="form-label">{t('Status')}</label>
          <select
            className="form-select"
            value={edit.status ?? 'active'}
            onChange={(e) => field('status', e.target.value as crm.Client['status'])}
          >
            {CLIENT_STATUSES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" title={t('The deck cover lockup renders it as «logo × NAVEN».')}>{t('Logo')}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {detail.logo_key ? (
            <span style={{ background: '#0B0C0E', borderRadius: 4, padding: '8px 14px', display: 'inline-flex' }}>
              <img
                src={`/api/clients/${detail.id}/logo?v=${logoVersion}`} alt=""
                style={{ height: 36, maxWidth: 200, objectFit: 'contain' }}
              />
            </span>
          ) : (
            <span className="n-none">{t('no logo on the record')}</span>
          )}
          <button type="button" className="btn btn-sm" disabled={logoBusy} onClick={() => logoFileRef.current?.click()}>
            {detail.logo_key ? t('Replace logo…') : t('Upload logo…')}
          </button>
          {detail.logo_key && (
            <button type="button" className="btn btn-sm" disabled={logoBusy} onClick={() => void removeLogo()}>
              {t('Remove')}
            </button>
          )}
          <input
            type="file" accept="image/png,image/jpeg" hidden ref={logoFileRef}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pickLogo(f); }}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('Legal entity')}</label>
        <input
          className="form-input"
          placeholder={t('e.g. {name} Holdings Limited — the party that signs', { name: edit.name ?? 'Acme' })}
          value={edit.legal_name ?? ''}
          onChange={(e) => field('legal_name', e.target.value)}
        />
        <div className="text-xs text-ink-3 mt-4">
          {t('Printed on the SOW as the contracting party. Leave empty if it is the same as the name above.')}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">{t('Address line 1')}</label>
        <input className="form-input" placeholder={t('Building or house')}
          value={edit.address_line1 ?? ''} onChange={(e) => field('address_line1', e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">{t('Address line 2')}</label>
        <input className="form-input" placeholder={t('Street')}
          value={edit.address_line2 ?? ''} onChange={(e) => field('address_line2', e.target.value)} />
      </div>
      <div className="flex gap-12">
        <div className="form-group" style={{ width: 140 }}>
          <label className="form-label">{t('Post code')}</label>
          <input className="form-input" value={edit.postcode ?? ''}
            onChange={(e) => field('postcode', e.target.value)} />
        </div>
        <div className="form-group flex-1">
          <label className="form-label">{t('City')}</label>
          <input className="form-input" value={edit.city ?? ''}
            onChange={(e) => field('city', e.target.value)} />
        </div>
        <div className="form-group flex-1">
          <label className="form-label">{t('Country')}</label>
          <select className="form-select" value={edit.country ?? ''}
            onChange={(e) => field('country', e.target.value)}>
            <option value="">—</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">{t('Notes')}</label>
        <textarea
          className="form-textarea"
          rows={3}
          value={edit.notes ?? ''}
          onChange={(e) => field('notes', e.target.value)}
        />
      </div>
      <div className="flex gap-8 mb-8">
        <button className="btn btn-accent" disabled={!dirty || saving} onClick={() => void saveClient()}>
          {saving ? t('Saving…') : t('Save changes')}
        </button>
        {/* Archive is the normal end of a real client and keeps everything, so it is
            the plain button in the plain place. Delete is elsewhere and red. */}
        <button
          className="btn ml-auto"
          title={t('Keeps every engagement, contact and document; the client leaves the active list')}
          disabled={detail.status === 'archived'}
          onClick={() => setConfirm({ kind: 'archive-client', id: detail.id, label: detail.name })}
        >
          <Archive className="icon-sm" /> {detail.status === 'archived' ? t('Archived') : t('Archive')}
        </button>
      </div>
      {/* Delete lives below the fold of the normal actions, quieter and unmistakably
          different — a mistaken row goes, a finished engagement gets archived. */}
      <div
        className="flex items-center justify-between gap-12 mb-24 p-12"
        style={{ border: '1px solid var(--line)', borderRadius: 'var(--r2)' }}
      >
        <div className="text-xs text-ink-3" style={{ lineHeight: 1.5 }}>
          {t('Created this by mistake? Deleting removes the client and everything under it — engagements, contacts, portal users — immediately and for good.')}
        </div>
        <button
          className="btn btn-sm btn-danger shrink-0"
          onClick={() => void askDeleteClient()}
        >
          <Trash2 className="icon-sm" /> {t('Delete client')}
        </button>
      </div>

      {/* ── Contact editor ── */}
      {contactDraft && (
        <div className="modal-overlay" onClick={() => setContactDraft(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{contactDraft.id ? t('Edit contact') : t('New contact')}</h2>
              <button className="close-btn" onClick={() => setContactDraft(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">{t('First name')}</label>
                  <input
                    className="form-input"
                    value={contactDraft.first_name ?? ''}
                    onChange={(e) => setContactDraft({ ...contactDraft, first_name: e.target.value })}
                  />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">{t('Family name')}</label>
                  <input
                    className="form-input"
                    value={contactDraft.last_name ?? ''}
                    onChange={(e) => setContactDraft({ ...contactDraft, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">{t('Role')}</label>
                  <input
                    className="form-input"
                    value={contactDraft.role_title ?? ''}
                    onChange={(e) => setContactDraft({ ...contactDraft, role_title: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">{t('Email')}</label>
                  <input
                    className="form-input"
                    type="email"
                    value={contactDraft.email ?? ''}
                    onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ width: 210 }}>
                  <label className="form-label">{t('Phone')}</label>
                  <input
                    className="form-input mono"
                    placeholder={detail.country ? t('e.g. 079 411 55 35') : t('e.g. +41 79 411 55 35')}
                    value={contactDraft.phone ?? ''}
                    onChange={(e) => { setContactDraft({ ...contactDraft, phone: e.target.value }); setPhoneNote(null); }}
                    onBlur={(e) => {
                      const r = normalisePhone(e.target.value, detail.country);
                      if (r.valid && r.display !== e.target.value) {
                        setContactDraft((d) => ({ ...d, phone: r.display }));
                      }
                      setPhoneNote(r.valid ? null : r.reason ?? null);
                    }}
                  />
                  {phoneNote
                    ? <div className="text-xs mt-4" style={{ color: 'var(--warn)' }}>{phoneNote}</div>
                    : <div className="text-xs text-ink-3 mt-4">{t('Stored in international format.')}</div>}
                </div>
              </div>
              <label className="flex items-center gap-8 text-sm text-ink-2 mb-12">
                <input
                  type="checkbox"
                  checked={!!contactDraft.is_sponsor}
                  onChange={(e) => setContactDraft({ ...contactDraft, is_sponsor: e.target.checked })}
                />
                {t('Sponsor — signs the SOW and acceptance records')}
              </label>
              <div className="form-group">
                <label className="form-label">{t('Notes')}</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={contactDraft.notes ?? ''}
                  onChange={(e) => setContactDraft({ ...contactDraft, notes: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setContactDraft(null)}>{t('Cancel')}</button>
              <button className="btn btn-accent" onClick={() => void saveContact()}>{t('Save contact')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.kind === 'archive-client' ? t('Archive client')
            : confirm?.plan.allowed === false ? t('{label} cannot be deleted', { label: confirm.label })
              : confirm?.kind === 'delete-client' ? t('Delete {label}', { label: confirm.label })
                : t('Delete {label}', { label: confirm?.label ?? t('contact') })
        }
        confirmLabel={
          confirm?.kind === 'archive-client' ? t('Archive')
            : confirm?.plan.allowed === false
              ? (confirm.kind === 'delete-client' ? t('Archive instead') : t('Got it'))
              : busy ? t('Deleting…') : t('Delete permanently')
        }
        danger={confirm?.kind !== 'archive-client' && confirm?.plan.allowed !== false}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runConfirm()}
      >
        {confirm?.kind === 'archive-client' && (
          <p className="text-sm text-ink-2">
            {t('Archive “{label}”? Engagements and history are preserved — the client simply drops out of the active list. Nothing is removed, and you can bring it back by setting the status to active.', { label: confirm.label })}
          </p>
        )}

        {confirm && confirm.kind !== 'archive-client' && !confirm.plan.allowed && (
          <>
            {confirm.plan.blockers.map((b) => (
              <p className="text-sm text-ink-2" key={b.code} style={{ lineHeight: 1.6 }}>{b.message}</p>
            ))}
          </>
        )}

        {confirm?.kind === 'delete-client' && confirm.plan.allowed && (
          <>
            <p className="text-sm text-ink-2">
              {t('Delete “{label}” outright? This is for a client that should never have existed — a real one that has finished gets archived instead.', { label: confirm.label })}
            </p>
            {confirm.plan.cascade.length > 0 ? (
              <ul className="text-sm text-ink-2 mt-8" style={{ paddingLeft: 18, lineHeight: 1.7 }}>
                {confirm.plan.cascade.map((c) => (
                  <li key={c.what}>{tx(t, '{count} {what} go with it', { count: <span className="mono">{c.count}</span>, what: c.what })}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-ink-3 mt-8">{t('Nothing else hangs off this client.')}</p>
            )}
            {confirm.plan.engagements.length > 0 && (
              <p className="text-sm mt-8" style={{ color: 'var(--warn)', lineHeight: 1.6 }}>
                {tx(t, 'Including {codes} with every requirement and run inside them — and the client’s document library, stored files included.', {
                  codes: confirm.plan.engagements.map((e) => <span className="mono" key={e.id}>{e.code} </span>),
                })}
              </p>
            )}
            <p className="text-xs text-ink-3 mt-8">{t('Immediate and irreversible. The audit log keeps the record.')}</p>
          </>
        )}

        {confirm?.kind === 'delete-contact' && confirm.plan.allowed && (
          <>
            <p className="text-sm text-ink-2">
              {t('Delete “{label}”? The full record is written to the audit log before removal.', { label: confirm.label })}
            </p>
            {confirm.plan.cascade.map((c) => (
              <p className="text-xs text-ink-3 mt-8" key={c.what}>{c.what}.</p>
            ))}
          </>
        )}
      </ConfirmModal>
    </>
  );
}
